// apps/tmg-web/components/sitewide-footer.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import { Icons } from "./icons";
import { siteConfig } from "@/site.config";
import { ButtonLink } from "./ui/button-link";

export interface FooterLink {
  href: string;
  label: string;
}

interface SiteWideFooterProps {
  footerLinks?: FooterLink[];
  cta?: {
    href: string;
    label: string;
    variant?: "highlight" | "default" | "outline" | "link";
  };
}

export default function SiteWideFooter({
  footerLinks,
  cta,
}: SiteWideFooterProps) {
  const links = footerLinks || [
    { href: "/about", label: "About" },
    { href: "/approach", label: "Approach" },
    { href: "/sectors", label: "Sectors" },
    { href: "/why-tmg", label: "Why TMG" },
    { href: "/contact", label: "Contact" },
  ];

  const ctaProps = cta || {
    href: "/contact",
    label: "Partner With Us",
    variant: "highlight" as const,
  };

  return (
    <footer className="w-full border-t bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row justify-between items-center gap-6">
        {/* Left: Site Info */}
        <div className="flex items-center space-x-3">
          <Icons.logo className="size-20 text-primary" />
          <span className="font-semibold text-lg text-primary">
            {siteConfig.name}
          </span>
        </div>

        {/* Center: Footer nav links */}
        <nav
          className="flex flex-wrap justify-center gap-4 md:gap-6"
          aria-label="Footer"
        >
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-highlight"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right: CTA */}
        <ButtonLink href={ctaProps.href} variant={ctaProps.variant} size="sm">
          {ctaProps.label}
        </ButtonLink>
      </div>

      {/* Bottom copyright */}
      <div className="text-center text-xs text-muted-foreground pb-4">
        &copy; {new Date().getFullYear()} {siteConfig.name}. All rights
        reserved.
      </div>
    </footer>
  );
}
